import { useEmbeddedPaymentBlock } from "@/hooks/use-embedded-payment-block";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Clock,
  Copy,
  CreditCard,
  Loader2,
  QrCode,
  Sparkles,
  Ticket,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RenewalCouponField } from "@/components/billing/RenewalCouponField";
import { RenewedCycleNotice } from "@/components/billing/RenewedCycleNotice";
import { CancelPendingHoldButton } from "@/components/wallet/CancelPendingHoldButton";
import {
  useCreatePixRenewalOrderMutation,
  useMyCpmWalletQuery,
  useMyDataQuery,
  useRenewPlanWithWalletMutation,
  useStartRenewedCycleNowMutation,
} from "@/generated/graphql";
import { useToast } from "@/hooks/use-toast";
import { authAxios } from "@/lib/axios";
import { refetchAllMyData } from "@/lib/apollo/refetchAllMyData";
import { trackPurchaseCompleted } from "@/lib/analytics/trackPurchaseCompleted";
import { computeRenewalPriceCents } from "@/lib/billing/computeRenewalPriceCents";
import { describeRenewalCoupon } from "@/lib/billing/describeRenewalCoupon";
import { pendingCycleResetAt } from "@/lib/billing/pendingCycleResetAt";
import { waitForRenewalApplied } from "@/lib/billing/waitForRenewalApplied";
import { computeWalletPlanCharge } from "@/lib/billing/computeWalletPlanCharge";
import { walletPlanDiscountPercent } from "@/lib/billing/walletPlanDiscountPercent";
import { RedeemPixPayload, resolveRedeemOutcome } from "@/lib/billing/resolveRedeemOutcome";
import { formatCents } from "@/lib/format/currency";

interface PixRenewalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void | Promise<void>;
  autoGenerate?: boolean;
  variant?: 'pix' | 'grant';
  initialCouponCode?: string | null;
  openCouponField?: boolean;
  onCouponApplied?: () => unknown;
  /**
   * The new limits start as soon as the payment lands (the "limit reached,
   * renew early" prompt). Otherwise an early payment keeps the current limits
   * until the due date and the paid screen offers to start them now.
   */
  resetNow?: boolean;
}

interface RenewalSubscription {
  payment_recurrency?: string | null;
  pending_cycle_reset_at?: string | null;
  pagarme_last_order_id?: string | null;
  next_payment_at?: string | null;
  coupon_metadata?: {
    code?: string | null;
    type: string;
    month_discount?: number | string | null;
    year_discount?: number | string | null;
    month_cycles?: number | string | null;
    year_cycles?: number | string | null;
  } | null;
  plan?: {
    name?: string | null;
    slug?: string | null;
    value?: number | string | null;
    value_yearly?: number | string | null;
  } | null;
}

interface PixPaymentData {
  id: string;
  amount: number;
  status: string;
  pix_code?: string | null;
  pix_qr_code_url?: string | null;
  expires_at?: string | null;
}

type Step = 'select' | 'pix-form' | 'pix-payment' | 'wallet-confirm' | 'success';

const formatCPFCNPJ = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return digits
    .slice(0, 14)
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

const PixRenewalModalContent = ({
  open,
  onOpenChange,
  onSuccess,
  autoGenerate,
  variant = 'pix',
  initialCouponCode,
  openCouponField,
  onCouponApplied,
  resetNow,
}: PixRenewalModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data: myData, loading: myDataLoading, refetch } = useMyDataQuery();
  const {
    data: walletData,
    error: walletError,
    loading: walletLoading,
    refetch: refetchWallet,
  } = useMyCpmWalletQuery();
  const [createPixRenewalOrder] = useCreatePixRenewalOrderMutation();
  const [renewPlanWithWallet] = useRenewPlanWithWalletMutation();
  const [startRenewedCycleNow] = useStartRenewedCycleNowMutation();

  const [step, setStep] = useState<Step>('select');
  const [walletMode, setWalletMode] = useState(false);
  const [documentNumber, setDocumentNumber] = useState('');
  const [pixRenewalData, setPixRenewalData] = useState<PixPaymentData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [couponFieldOpen, setCouponFieldOpen] = useState(!!openCouponField || !!initialCouponCode);
  const [autoApplyConsumed, setAutoApplyConsumed] = useState(false);
  const [couponPending, setCouponPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Decided once the payment is applied: offer "start new limits now".
  const [startNowOffered, setStartNowOffered] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);
  // The dialog content (and the field with it) remounts on every open, so the
  // field's starting state must be right before that render, not in an effect.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setCouponFieldOpen(!!openCouponField || !!initialCouponCode);
  }
  const pixPollingRef = useRef<NodeJS.Timeout | null>(null);
  const autoGenerateAttemptedRef = useRef(false);
  const generatePixRenewalRef = useRef<() => Promise<void>>(async () => undefined);
  // The paid screen offers "start new limits now": don't auto-close it.
  const keepOpenForNoticeRef = useRef(false);

  const subscription = myData?.myData?.subscription as RenewalSubscription | null | undefined;
  const rawRenewalPrice = subscription?.payment_recurrency === 'yearly'
    ? subscription?.plan?.value_yearly
    : subscription?.plan?.value;
  const hasKnownRenewalPrice = rawRenewalPrice !== null
    && rawRenewalPrice !== undefined
    && Number.isFinite(Number(rawRenewalPrice))
    && Number(rawRenewalPrice) >= 0;
  const renewalPriceCents = computeRenewalPriceCents({
    plan: subscription?.plan ?? {},
    paymentRecurrency: subscription?.payment_recurrency ?? 'monthly',
    couponMetadata: subscription?.coupon_metadata,
  });
  const couponBreakdown = describeRenewalCoupon({
    plan: subscription?.plan,
    paymentRecurrency: subscription?.payment_recurrency,
    couponMetadata: subscription?.coupon_metadata,
  });
  const couponSettled = !initialCouponCode || autoApplyConsumed;
  // Paying while a coupon applies could charge the old price or race the
  // coupon's write to the subscription, so every payment control waits.
  const couponBusy = couponPending || !couponSettled;
  const walletAvailableCents = walletError ? 0 : (walletData?.myCpmWallet?.available_cents ?? 0);
  const canUseWallet = hasKnownRenewalPrice && walletAvailableCents > 0;
  const isRedemptionHoldPending = walletData?.myCpmWallet?.pending_kind === 'plan_redemption'
    || walletData?.myCpmWallet?.pending_kind === 'minutes_redemption';
  const walletDiscountPercent = walletPlanDiscountPercent(subscription?.plan);
  const walletApplication = computeWalletPlanCharge({
    availableCents: walletAvailableCents,
    priceCents: renewalPriceCents,
    discountPercent: walletDiscountPercent,
  });

  const stopPixPolling = () => {
    if (pixPollingRef.current) {
      clearInterval(pixPollingRef.current);
      pixPollingRef.current = null;
    }
  };

  const resetModal = () => {
    autoGenerateAttemptedRef.current = false;
    keepOpenForNoticeRef.current = false;
    setStartNowOffered(false);
    setStep('select');
    setWalletMode(false);
    setDocumentNumber('');
    setPixRenewalData(null);
    setIsLoading(false);
    setCouponFieldOpen(!!openCouponField || !!initialCouponCode);
    setAutoApplyConsumed(false);
  };

  const handleClose = () => {
    stopPixPolling();
    onOpenChange(false);
    resetModal();
  };

  const walletErrorToast = (message: string) => {
    const known: Record<string, string> = {
      wallet_balance_too_low: t('checkout.walletErrorBalanceTooLow'),
      payout_request_already_pending: t('checkout.walletErrorPayoutPending'),
      remainder_method_required: t('checkout.walletErrorRemainderRequired'),
      renewal_not_due: t('checkout.walletErrorRenewalNotDue'),
      not_eligible: t('checkout.walletErrorNotEligible'),
      phone_invalid: t('checkout.invalidPhoneDesc'),
    };
    const match = Object.keys(known).find((code) => message.includes(code));
    toast({
      title: t('account.renewPixError'),
      description: match ? known[match] : t('account.renewPixError'),
      variant: "destructive",
    });
  };

  // The Sidebar and the host page hold their own myData (no-cache observers):
  // refresh them all, not only this modal's.
  const refreshAfterSuccess = async () => Promise.allSettled([
    refetch(),
    refetchWallet(),
    Promise.resolve().then(() => onSuccess?.()),
    Promise.resolve().then(() => refetchAllMyData()),
  ]);

  // Offer "start new limits now" when the freshly read subscription still has
  // a deferred reset ahead, and keep the success screen open for it. Decided
  // from that read, not from whether a start was attempted: a start answered
  // with no_pending_cycle may still leave the reset pending.
  const offerStartNow = (
    myDataRefresh: PromiseSettledResult<Awaited<ReturnType<typeof refetch>>>,
  ) => {
    const freshSubscription = myDataRefresh.status === 'fulfilled'
      ? myDataRefresh.value.data?.myData?.subscription as RenewalSubscription | null | undefined
      : null;
    const pending = pendingCycleResetAt(freshSubscription, new Date()) !== null;
    setStartNowOffered(pending);
    keepOpenForNoticeRef.current = pending;
  };

  const closeAfterSuccess = () => {
    if (!keepOpenForNoticeRef.current) handleClose();
  };

  // Ask for the new cycle now. no_pending_cycle is expected when the payment
  // itself reset the limits; the refreshed subscription decides on the notice.
  const startRenewedCycleAfterPayment = async () => {
    try {
      await startRenewedCycleNow();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '';
      if (message.includes('no_pending_cycle')) return;
      console.error('Error starting the renewed cycle after payment:', error);
      setErrorMessage(t('billing.renewedCycle.error'));
    }
  };

  const completeWalletRenewal = async () => {
    setStep('success');
    toast({ title: t('account.renewWalletSuccess') });
    setTimeout(closeAfterSuccess, 2000);
    const [myDataRefresh] = await refreshAfterSuccess();
    offerStartNow(myDataRefresh);
  };

  const completePixPayment = async (
    orderId: string,
    isWalletRemainder: boolean,
    before: RenewalSubscription | null | undefined,
  ) => {
    if (isWalletRemainder) {
      setStep('success');
      toast({ title: t('account.renewWalletSuccess') });
    } else {
      setPixRenewalData((previous) => previous ? { ...previous, status: 'paid' } : null);
      toast({
        title: t('account.renewPixSuccess'),
        description: t('account.renewPixSuccess'),
      });
    }
    // Pagar.me reports "paid" before our webhook applies the renewal. A wallet
    // remainder order is never stored as pagarme_last_order_id.
    const { applied } = await waitForRenewalApplied({
      orderId: isWalletRemainder ? null : orderId,
      before,
      refetch: async () => (await refetch())?.data?.myData?.subscription as RenewalSubscription | null | undefined,
    });
    // An open order is reused whatever its reset timing, so ask for the reset
    // explicitly, but only once the payment is applied.
    if (resetNow && applied) await startRenewedCycleAfterPayment();
    const [myDataRefresh] = await refreshAfterSuccess();
    if (!isWalletRemainder && myDataRefresh.status === 'fulfilled') {
      trackPurchaseCompleted(myDataRefresh.value.data?.myData?.subscription);
    }
    // Still deferred (not asked for, unconfirmed, failed, or no_pending_cycle
    // with the reset still ahead): the customer chooses with the notice.
    offerStartNow(myDataRefresh);
    setTimeout(closeAfterSuccess, 2000);
  };

  const startPixPolling = (orderId: string, isWalletRemainder: boolean) => {
    stopPixPolling();
    const before = subscription;
    let attempts = 0;
    const maxAttempts = 120;

    pixPollingRef.current = setInterval(async () => {
      attempts++;
      if (attempts >= maxAttempts) {
        stopPixPolling();
        return;
      }

      try {
        const response = await authAxios.get(`/api/subscriptions/pix/${orderId}`);
        const status = response.data?.status;

        if (status === 'paid') {
          stopPixPolling();
          await completePixPayment(orderId, isWalletRemainder, before);
        } else if (status === 'expired' || status === 'canceled' || status === 'failed') {
          stopPixPolling();
          setPixRenewalData((previous) => previous ? { ...previous, status } : null);
        }
      } catch (error) {
        console.error('Error polling PIX status:', error);
      }
    }, 5000);
  };

  const handleGeneratePixRenewal = async () => {
    setIsLoading(true);
    setWalletMode(false);
    setPixRenewalData(null);

    try {
      const { data } = await createPixRenewalOrder({ variables: { resetNow: !!resetNow } });
      if (data?.createPixRenewalOrder) {
        setPixRenewalData(data.createPixRenewalOrder);
        setStep('pix-payment');
        startPixPolling(data.createPixRenewalOrder.id, false);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '';
      console.error('Error generating PIX renewal:', error);
      toast({
        title: t('account.renewPixError'),
        description: message || t('account.renewPixError'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  generatePixRenewalRef.current = handleGeneratePixRenewal;

  const handleWalletFullConfirm = async () => {
    setIsLoading(true);
    try {
      const { data } = await renewPlanWithWallet();
      const outcome = resolveRedeemOutcome(data?.renewPlanWithWallet);
      if (outcome.action === 'completed') {
        await completeWalletRenewal();
      } else {
        walletErrorToast('');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '';
      console.error('Error renewing plan with wallet:', error);
      walletErrorToast(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWalletCard = async () => {
    setIsLoading(true);
    try {
      const { data } = await renewPlanWithWallet({
        variables: {
          remainderMethod: 'credit_card',
          returnUrl: window.location.href,
        },
      });
      const outcome = resolveRedeemOutcome(data?.renewPlanWithWallet);
      if (outcome.action === 'redirect') {
        window.location.href = outcome.url;
        return;
      }
      walletErrorToast('');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '';
      console.error('Error creating renewal card remainder:', error);
      walletErrorToast(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWalletPix = async () => {
    const cleanDoc = documentNumber.replace(/\D/g, '');
    if (cleanDoc.length !== 11 && cleanDoc.length !== 14) return;

    setIsLoading(true);
    try {
      const { data } = await renewPlanWithWallet({
        variables: {
          remainderMethod: 'pix',
          document: {
            number: cleanDoc,
            type: cleanDoc.length === 11 ? 'cpf' : 'cnpj',
          },
        },
      });
      const outcome = resolveRedeemOutcome(data?.renewPlanWithWallet);
      if (outcome.action !== 'pix') {
        walletErrorToast('');
        return;
      }

      const pix: RedeemPixPayload = outcome.pix;
      const orderId = String(pix.id);
      setPixRenewalData({
        id: orderId,
        amount: walletApplication.remainingCents / 100,
        status: 'pending',
        pix_code: pix.qr_code,
        pix_qr_code_url: pix.qr_code_url,
        expires_at: pix.expires_at,
      });
      setStep('pix-payment');
      startPixPolling(orderId, true);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '';
      console.error('Error creating renewal PIX remainder:', error);
      walletErrorToast(message);
    } finally {
      setIsLoading(false);
    }
  };

  const copyPixCode = async () => {
    if (!pixRenewalData?.pix_code) return;
    try {
      await navigator.clipboard.writeText(pixRenewalData.pix_code);
      toast({
        title: t('account.renewPixCodeCopied'),
        description: t('account.renewPixCodeCopied'),
      });
    } catch (error) {
      console.error('Failed to copy PIX code:', error);
    }
  };

  const formatPixExpiration = (dateString: string) => new Date(dateString).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  useEffect(() => {
    if (
      open
      && autoGenerate
      && variant === 'pix'
      && !walletLoading
      && walletAvailableCents === 0
      && step === 'select'
      && !pixRenewalData
      && !isLoading
      && !couponBusy
      && !autoGenerateAttemptedRef.current
    ) {
      autoGenerateAttemptedRef.current = true;
      void generatePixRenewalRef.current();
    }
  }, [
    autoGenerate,
    couponBusy,
    isLoading,
    open,
    pixRenewalData,
    step,
    variant,
    walletAvailableCents,
    walletLoading,
  ]);

  useEffect(() => {
    if (!open) {
      stopPixPolling();
      autoGenerateAttemptedRef.current = false;
    }
    return stopPixPolling;
  }, [open]);

  const handleCouponApplied = async () => {
    await Promise.allSettled([
      refetch(),
      refetchWallet(),
      Promise.resolve().then(() => onCouponApplied?.()),
    ]);
  };

  const renewedCycleNotice = !startNowOffered ? null : (
    <RenewedCycleNotice
      subscription={subscription}
      onStarted={() => { void refreshAfterSuccess(); }}
      className="mt-4"
    />
  );

  const handleBackToCoupon = () => {
    stopPixPolling();
    setPixRenewalData(null);
    setCouponFieldOpen(true);
    setStep('select');
  };

  const renderPixPayment = () => {
    if (!pixRenewalData) return null;
    if (pixRenewalData.status === 'paid') {
      return (
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-500" />
          </div>
          <p className="text-lg font-semibold text-green-500">{t('account.renewPixSuccess')}</p>
          {renewedCycleNotice}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground text-center">{t('account.renewPixDescription')}</p>
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            {walletMode ? t('account.buyExtraMinutesWalletRemainder') : t('account.renewPixAmount')}
          </p>
          <p className="text-2xl font-bold text-primary">
            {pixRenewalData.amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </p>
          {walletMode && (
            <p className="text-xs text-lime-600 dark:text-lime-400 font-medium">
              {t('account.buyExtraMinutesWalletApplied')}: − {formatCents(walletApplication.appliedCents, 'BRL')}
            </p>
          )}
        </div>

        {pixRenewalData.pix_qr_code_url && (
          <div className="flex justify-center">
            <div className="bg-white p-3 rounded-lg">
              <img src={pixRenewalData.pix_qr_code_url} alt="QR Code PIX" className="w-48 h-48" />
            </div>
          </div>
        )}

        {pixRenewalData.pix_code && (
          <div className="space-y-2">
            <div className="bg-muted p-3 rounded-lg">
              <p className="text-xs font-mono text-muted-foreground break-all line-clamp-3">
                {pixRenewalData.pix_code}
              </p>
            </div>
            <Button variant="outline" className="w-full" onClick={copyPixCode}>
              <Copy className="w-4 h-4 mr-2" />
              {t('account.renewPixCopyCode')}
            </Button>
          </div>
        )}

        {pixRenewalData.expires_at && (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>
              {t('account.renewPixExpires')}: {formatPixExpiration(pixRenewalData.expires_at)}
            </span>
          </div>
        )}

        <div className="flex items-center justify-center gap-2 text-sm text-primary">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>{t('account.renewPixWaiting')}</span>
        </div>

        {!walletMode && (
          <Button variant="ghost" size="sm" className="w-full" onClick={handleBackToCoupon}>
            <Ticket className="w-4 h-4 mr-2" />
            {t('renewalCoupon.toggle')}
          </Button>
        )}
      </div>
    );
  };

  const renderSelect = () => (
    <div className="space-y-4">
      {hasKnownRenewalPrice ? (
        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          {couponBreakdown && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('renewalCoupon.planPrice')}</span>
                <span className="font-semibold">{formatCents(couponBreakdown.planPriceCents, 'BRL')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {couponBreakdown.code
                    ? t('renewalCoupon.couponDiscount', { code: couponBreakdown.code })
                    : t('renewalCoupon.couponDiscountNoCode')}
                  <span className="block text-xs">
                    {couponBreakdown.cyclesLeft === -1
                      ? t('renewalCoupon.cyclesUnlimited')
                      : t('renewalCoupon.cyclesLeft', { count: couponBreakdown.cyclesLeft })}
                  </span>
                </span>
                <span className="font-semibold text-lime-600 dark:text-lime-400">
                  − {formatCents(couponBreakdown.couponDiscountCents, 'BRL')}
                </span>
              </div>
            </>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('account.buyExtraMinutesTotal')}</span>
            <span className="font-semibold">{formatCents(renewalPriceCents, 'BRL')}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('account.buyExtraMinutesWalletAvailable')}</span>
            <span className="font-semibold">{formatCents(walletAvailableCents, 'BRL')}</span>
          </div>
        </div>
      ) : (
        <p className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-amber-600 dark:text-amber-400">
          {t('account.renewWalletPriceUnavailable')}
        </p>
      )}

      <RenewalCouponField
        initialCode={initialCouponCode}
        autoApply={!!initialCouponCode && !autoApplyConsumed}
        defaultOpen={couponFieldOpen}
        disabled={isLoading}
        onApplied={handleCouponApplied}
        onAutoApplySettled={() => setAutoApplyConsumed(true)}
        onPendingChange={setCouponPending}
      />

      {!canUseWallet && isRedemptionHoldPending && (
        <div className="p-3 rounded-lg border border-amber-500/40 bg-amber-500/5 space-y-2">
          <p className="text-xs text-amber-600 dark:text-amber-400">{t('wallet.pendingHoldNotice')}</p>
          <CancelPendingHoldButton className="w-full" onCancelled={async () => { await refetchWallet(); }} />
        </div>
      )}

      {canUseWallet && (
        <div className="p-4 rounded-lg bg-lime-500/10 border border-lime-500/30 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-lime-600 dark:text-lime-400">
            <Wallet className="w-4 h-4" />
            {t('account.buyExtraMinutesWalletTitle')}
          </div>
          {walletApplication.coversFully ? (
            <Button
              data-testid="renew-plan-wallet-pay"
              className="w-full bg-lime-400 hover:bg-lime-500 text-lime-950 font-semibold"
              onClick={() => setStep('wallet-confirm')}
              disabled={isLoading || couponBusy}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {t('account.buyExtraMinutesWalletPayFull')}
            </Button>
          ) : (
            <>
              {walletApplication.discountCents > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t('checkout.walletDiscountAmount', { percent: walletDiscountPercent })}
                  </span>
                  <span className="font-medium text-lime-600 dark:text-lime-400">
                    − {formatCents(walletApplication.discountCents, 'BRL')}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('account.buyExtraMinutesWalletRemainder')}</span>
                <span className="font-medium">{formatCents(walletApplication.remainingCents, 'BRL')}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  className="bg-lime-400 hover:bg-lime-500 text-lime-950 font-semibold"
                  onClick={handleWalletCard}
                  disabled={isLoading || couponBusy}
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                  {t('account.buyExtraMinutesWalletPlusCard')}
                </Button>
                <Button
                  variant="outline"
                  className="border-lime-500/40 hover:bg-lime-500/10"
                  onClick={() => {
                    setWalletMode(true);
                    setStep('pix-form');
                  }}
                  disabled={isLoading || couponBusy}
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  {t('account.buyExtraMinutesWalletPlusPix')}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">{t('checkout.walletHoldNote')}</p>
            </>
          )}
        </div>
      )}

      {variant === 'pix' ? (
        <Button variant="outline" className="w-full" onClick={handleGeneratePixRenewal} disabled={isLoading || couponBusy}>
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <QrCode className="w-4 h-4 mr-2" />}
          {t('account.buyExtraMinutesPayPix')}
        </Button>
      ) : !canUseWallet && hasKnownRenewalPrice ? (
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={handleWalletCard} disabled={isLoading || walletLoading || isRedemptionHoldPending || couponBusy}>
            <CreditCard className="w-4 h-4 mr-2" />
            {t('account.renewPayCard')}
          </Button>
          <Button variant="outline" disabled={isLoading || walletLoading || isRedemptionHoldPending || couponBusy} onClick={() => {
            setWalletMode(true);
            setStep('pix-form');
          }}>
            <QrCode className="w-4 h-4 mr-2" />
            {t('account.buyExtraMinutesPayPix')}
          </Button>
        </div>
      ) : !canUseWallet ? (
        <Button asChild className="w-full">
          <Link to="/plans" onClick={handleClose}>{t('account.renewWalletGoPlans')}</Link>
        </Button>
      ) : null}
    </div>
  );

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 'pix-payment' && !walletMode ? <QrCode className="w-5 h-5" /> : <Wallet className="w-5 h-5" />}
            {step === 'pix-payment' && !walletMode ? t('account.renewPixTitle') : t('account.renewWalletTitle')}
          </DialogTitle>
        </DialogHeader>

        {(walletLoading || myDataLoading) && step === 'select' ? (
          <div className="text-center py-6">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-sm text-muted-foreground">{t('account.loading')}</p>
          </div>
        ) : step === 'success' ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-500" />
            </div>
            <p className="text-lg font-semibold text-green-500">{t('account.renewWalletSuccess')}</p>
            {renewedCycleNotice}
          </div>
        ) : step === 'pix-payment' ? renderPixPayment() : step === 'pix-form' ? (
          <div className="space-y-4">
            <Button
              variant="ghost"
              size="sm"
              className="p-0 h-auto text-muted-foreground"
              onClick={() => {
                setWalletMode(false);
                setStep('select');
              }}
              disabled={isLoading}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('account.buyExtraMinutesBack')}
            </Button>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">{t(walletApplication.appliedCents > 0 ? 'account.buyExtraMinutesWalletRemainder' : 'account.renewPixAmount')}</p>
              <p className="text-2xl font-bold text-primary">
                {formatCents(walletApplication.remainingCents, 'BRL')}
              </p>
              {walletApplication.appliedCents > 0 && (
                <p className="text-xs text-lime-600 dark:text-lime-400 font-medium">
                  {t('account.buyExtraMinutesWalletApplied')}: − {formatCents(walletApplication.appliedCents, 'BRL')}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>{t('account.buyExtraMinutesCpfCnpj')}</Label>
              <Input
                placeholder="000.000.000-00"
                value={documentNumber}
                onChange={(event) => setDocumentNumber(formatCPFCNPJ(event.target.value))}
                maxLength={18}
              />
            </div>
            <Button
              className="w-full"
              onClick={handleWalletPix}
              disabled={isLoading || documentNumber.replace(/\D/g, '').length < 11}
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <QrCode className="w-4 h-4 mr-2" />}
              {t('account.buyExtraMinutesGeneratePix')}
            </Button>
          </div>
        ) : step === 'wallet-confirm' ? (
          <div className="space-y-4">
            <Button
              variant="ghost"
              size="sm"
              className="p-0 h-auto text-muted-foreground"
              onClick={() => setStep('select')}
              disabled={isLoading}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('account.buyExtraMinutesBack')}
            </Button>
            <div className="p-4 rounded-lg bg-lime-500/10 border border-lime-500/30 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('account.buyExtraMinutesTotal')}</span>
                <span className="font-semibold">{formatCents(renewalPriceCents, 'BRL')}</span>
              </div>
              {walletApplication.discountCents > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t('checkout.walletDiscountAmount', { percent: walletDiscountPercent })}
                  </span>
                  <span className="font-semibold text-lime-600 dark:text-lime-400">
                    − {formatCents(walletApplication.discountCents, 'BRL')}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('checkout.walletBalanceApplied')}</span>
                <span className="font-semibold text-lime-600 dark:text-lime-400">
                  − {formatCents(walletApplication.appliedCents, 'BRL')}
                </span>
              </div>
              <p className="text-sm font-semibold text-center pt-1 border-t border-lime-500/20">
                {t('checkout.walletFullCoverage')}
              </p>
            </div>
            <Button
              className="w-full bg-lime-400 hover:bg-lime-500 text-lime-950 font-semibold"
              onClick={handleWalletFullConfirm}
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {t('checkout.walletConfirmButton')}
            </Button>
          </div>
        ) : renderSelect()}
      </DialogContent>
    </Dialog>
    <Dialog open={!!errorMessage} onOpenChange={() => setErrorMessage(null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('common.error')}</DialogTitle>
          <DialogDescription>{errorMessage}</DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
    </>
  );
};

const PixRenewalModal = (props: React.ComponentProps<typeof PixRenewalModalContent>) => {
  const embedded = useEmbeddedPaymentBlock(props.open, props.onOpenChange);
  return embedded ? null : <PixRenewalModalContent {...props} />;
};

export default PixRenewalModal;
